/**
 * jsonMergeUtil_Boolean.ts - Boolean value parsing utilities
 * Port of C# JsonMergeUtil_Boolean.cs
 */

import { JsonValue } from '../../app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';

export interface BooleanResult {
    found: boolean;
    result: boolean;
}

/**
 * Broad boolean coercion: JSON Bool → bool, JSON String parsable as bool → bool,
 * JSON Integer → !=0. All other JsonValue shapes return found=false.
 */
export function tryGetBooleanValue(value: JsonValue): BooleanResult {
    if (value.isBool) {
        return { found: true, result: value.getBool() };
    }
    if (value.isString) {
        const s = value.getString().toLowerCase();
        if (s === 'true') return { found: true, result: true };
        if (s === 'false') return { found: true, result: false };
    }
    if (value.isInteger) {
        return { found: true, result: value.getInteger() !== 0 };
    }
    return { found: false, result: false };
}

/**
 * Strict JSON bool check: returns found=true only when the JsonValue is the Bool type.
 * Used by callers that must distinguish "the JSON literally said true/false" from
 * the broader truthiness rules (e.g., array conditional dispatch).
 */
export function tryGetStrictBool(value: JsonValue): BooleanResult {
    if (value.isBool) {
        return { found: true, result: value.getBool() };
    }
    return { found: false, result: false };
}

/**
 * Extended truthiness check for {{?Key}} conditional markers.
 * Supports: bool, integer, number, string, array (empty/filled), object (exists), null.
 */
export function tryGetTruthy(value: JsonValue): BooleanResult {
    const bool = tryGetBooleanValue(value);
    if (bool.found) return bool;

    if (value.isNumber) {
        return { found: true, result: value.getNumber() !== 0 };
    }
    if (value.isArray) {
        const arr = value.asArray();
        return { found: true, result: arr !== null && arr.length > 0 };
    }
    if (value.isObject) {
        return { found: true, result: true };
    }
    if (value.isString) {
        const s = value.getString();
        const result = s !== '' && s.toLowerCase() !== '0' && s.toLowerCase() !== 'false';
        if (s !== null && s.includes('{{')) {
            Logger.fallback("F-COE2", `{{?Key}} truthiness encountered an unresolved-placeholder string: value="${s}"`);
            throw new Error(`Cannot coerce unresolved placeholder string to boolean: ${s}`);
        }
        return {
            found: true,
            result
        };
    }
    if (value.isNull) {
        return { found: true, result: false };
    }
    return { found: false, result: false };
}

// Backwards-name aliases used elsewhere in node (will be removed once all callsites migrate).
export const booleanMethods = {
    tryGetBooleanValue,
    tryGetStrictBool,
    tryGetTruthy,
};
